import { VariantUpdateEvent } from '@theme/events';

/**
 * Watches for URL changes and triggers variant price updates
 * when external apps (like TeeInBlue) change variants without using the native variant picker
 */
class UrlVariantWatcher {
  /** @type {string | null} */
  #currentVariantId = null;
  
  /** @type {string | null} */
  #currentProductUrl = null;

  /** @type {AbortController | undefined} */
  #abortController;

  constructor() {
    this.init();
  }

  init() {
    // Only run on product pages
    if (!this.isProductPage()) return;

    // Store initial state
    this.#currentVariantId = this.getCurrentVariantId();
    this.#currentProductUrl = this.getCurrentProductUrl();

    // Listen for URL changes (back/forward buttons)
    window.addEventListener('popstate', this.handleUrlChange.bind(this));

    // Listen for programmatic URL changes (pushState/replaceState)
    this.interceptHistoryMethods();

  }

  /**
   * Check if we're on a product page
   */
  isProductPage() {
    return window.location.pathname.includes('/products/');
  }

  /**
   * Get current variant ID from URL
   */
  getCurrentVariantId() {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('variant');
  }

  /**
   * Get current product URL without query parameters
   */
  getCurrentProductUrl() {
    return window.location.pathname;
  }

  /**
   * Intercept history.pushState and history.replaceState to detect programmatic URL changes
   */
  interceptHistoryMethods() {
    const originalPushState = history.pushState;
    const originalReplaceState = history.replaceState;

    history.pushState = (...args) => {
      originalPushState.apply(history, args);
      // Small delay to ensure URL is updated
      setTimeout(() => this.handleUrlChange(), 10);
    };

    history.replaceState = (...args) => {
      originalReplaceState.apply(history, args);
      // Small delay to ensure URL is updated
      setTimeout(() => this.handleUrlChange(), 10);
    };
  }

  /**
   * Handle URL changes and update prices if variant changed
   */
  handleUrlChange() {
    if (!this.isProductPage()) return;

    const newVariantId = this.getCurrentVariantId();
    const newProductUrl = this.getCurrentProductUrl();

    // Check if variant or product changed
    const variantChanged = newVariantId !== this.#currentVariantId;
    const productChanged = newProductUrl !== this.#currentProductUrl;

    if (variantChanged || productChanged) {
      // Update stored state
      this.#currentVariantId = newVariantId;
      this.#currentProductUrl = newProductUrl;

      // Trigger price update
      this.updatePriceFromUrl();
    }
  }

  /**
   * Fetch updated price data and trigger price update event
   */
  updatePriceFromUrl(variantId = this.#currentVariantId) {
    // Abort previous request if still pending
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    // Fetch variant data directly from Shopify Product API
    const productHandle = window.location.pathname.split('/').pop();
    const requestUrl = `${window.location.origin}/products/${productHandle}.js`;
    
    fetch(requestUrl, { signal: this.#abortController.signal })
      .then(response => response.json())
      .then(productData => {
        
        // Find the specific variant
        const variant = productData.variants.find((v) => v.id.toString() === (variantId || this.#currentVariantId));
        
        if (variant) {
          
          // Create variant data structure that matches what VariantUpdateEvent expects
          const variantData = {
            id: variant.id.toString(),
            price: variant.price,
            compare_at_price: variant.compare_at_price,
            available: variant.available,
            inventory_management: variant.inventory_management || false,
            title: variant.title,
            option1: variant.option1,
            option2: variant.option2,
            option3: variant.option3,
            featured_media: variant.featured_media,
            url: `${window.location.pathname}?variant=${variant.id}`
          };
          
          // Create proper HTML with updated price data
          const mockHtml = document.implementation.createHTMLDocument();
          const productPriceElement = document.querySelector('product-price');
          
          if (productPriceElement) {
            const showCompareAtPrice = productPriceElement.dataset.showCompareAtPrice !== 'false';
            const showSavingsBadge = productPriceElement.dataset.showSavingsBadge === 'true';
            const savingsBadgeTemplate = productPriceElement.dataset.savingsBadgeText || '-[percentage]';

            // Clone the current price element structure
            const newPriceElement = productPriceElement.cloneNode(true);
            
            // Find the price-with-savings container in the cloned element
            const priceWithSavings = newPriceElement.querySelector('.price-with-savings');
            
            if (priceWithSavings) {
              // Get current currency format from existing price elements
              const salePriceElement = priceWithSavings.querySelector('.price');
              const currentCompareElement = priceWithSavings.querySelector('.compare-at-price');
              
              // Extract currency format from current prices
              let currencyFormat = '€[amount]'; // Default fallback
              if (salePriceElement && salePriceElement.textContent) {
                const priceText = salePriceElement.textContent.trim();
                // Detect currency format (€29,99 or $29.99 or £29.99)
                if (priceText.includes('€')) {
                  currencyFormat = priceText.replace(/[\d,\.]+/, '[amount]');
                } else if (priceText.includes('$')) {
                  currencyFormat = priceText.replace(/[\d,\.]+/, '[amount]');
                } else if (priceText.includes('£')) {
                  currencyFormat = priceText.replace(/[\d,\.]+/, '[amount]');
                }
              }
              
              // Extract just the number from existing prices to preserve exact formatting
              const extractAndFormatPrice = (price, existingElement) => {
                if (!existingElement || !existingElement.textContent) {
                  // Fallback formatting
                  const amount = (price / 100).toFixed(2);
                  if (currencyFormat.includes(',')) {
                    return currencyFormat.replace('[amount]', amount.replace('.', ','));
                  } else {
                    return currencyFormat.replace('[amount]', amount);
                  }
                }
                
                // Replace only the numbers in the existing text, preserving everything else
                const existingText = existingElement.textContent;
                const newAmount = (price / 100).toFixed(2);
                
                // Match the decimal format of existing text
                const formattedAmount = existingText.includes(',') ? 
                  newAmount.replace('.', ',') : newAmount;
                
                // Replace the number part while preserving currency symbol and spacing
                return existingText.replace(/[\d,\.]+/, formattedAmount);
              };
              
              const formattedPrice = extractAndFormatPrice(variant.price, salePriceElement);
              const formattedComparePrice = variant.compare_at_price ? 
                extractAndFormatPrice(variant.compare_at_price, currentCompareElement) : null;
              
              // Calculate savings percentage
              const savingsPercentage = variant.compare_at_price && variant.compare_at_price > variant.price 
                ? Math.round(((variant.compare_at_price - variant.price) / variant.compare_at_price) * 100)
                : 0;

              const formatSavingsBadgeText = () => {
                const savingsAmount = variant.compare_at_price - variant.price;
                let amountSaved = '';

                if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
                  amountSaved = window.Shopify.formatMoney(savingsAmount);
                } else {
                  amountSaved = (savingsAmount / 100).toFixed(2);
                }

                return savingsBadgeTemplate
                  .replace('[percentage]', savingsPercentage)
                  .replace('[amount_saved]', amountSaved);
              };
              
              // Build the complete price HTML structure preserving original order
              let priceHtml = '';
              
              // Get the original price container to preserve structure and order
              const originalContainer = priceWithSavings.querySelector('[ref="priceContainer"]');
              
              if (showCompareAtPrice && formattedComparePrice && savingsPercentage > 0 && originalContainer) {
                // Clone the original structure to preserve order
                const containerClone = originalContainer.cloneNode(true);
                
                // Update the price values while preserving exact structure and formatting
                const priceSpan = containerClone.querySelector('.price');
                let compareSpan = containerClone.querySelector('.compare-at-price');
                
                // Only update the numbers, preserving all spacing, currency symbols, etc.
                if (priceSpan && priceSpan.textContent) {
                  const currentText = priceSpan.textContent;
                  const newAmount = (variant.price / 100).toFixed(2);
                  const formattedAmount = currentText.includes(',') ? newAmount.replace('.', ',') : newAmount;
                  priceSpan.textContent = currentText.replace(/[\d,\.]+/, formattedAmount);
                }
                
                // Handle compare price - only update numbers if it exists
                if (compareSpan && compareSpan.textContent && formattedComparePrice) {
                  const currentCompareText = compareSpan.textContent;
                  const newCompareAmount = (variant.compare_at_price / 100).toFixed(2);
                  const formattedCompareAmount = currentCompareText.includes(',') ? newCompareAmount.replace('.', ',') : newCompareAmount;
                  compareSpan.textContent = currentCompareText.replace(/[\d,\.]+/, formattedCompareAmount);
                } else if (!compareSpan && formattedComparePrice && priceSpan) {
                  // Create compare price element only if it doesn't exist
                  const priceGroup = priceSpan.closest('[role="group"]');
                  if (priceGroup) {
                    // Clone the price group structure to maintain exact formatting
                    const compareGroup = priceGroup.cloneNode(true);
                    const compareGroupPrice = compareGroup.querySelector('.price');
                    if (compareGroupPrice) {
                      compareGroupPrice.className = 'compare-at-price';
                      // Use the same text format as the original price but with compare price value
                      const originalText = priceSpan.textContent;
                      const newCompareAmount = (variant.compare_at_price / 100).toFixed(2);
                      const formattedCompareAmount = originalText.includes(',') ? newCompareAmount.replace('.', ',') : newCompareAmount;
                      compareGroupPrice.textContent = originalText.replace(/[\d,\.]+/, formattedCompareAmount);
                    }
                    const compareGroupLabel = compareGroup.querySelector('.visually-hidden');
                    if (compareGroupLabel) {
                      compareGroupLabel.textContent = Theme.translations.regular_price;
                    }
                    priceGroup.parentNode.insertBefore(compareGroup, priceGroup.nextSibling);
                  } else {
                    // Simple span creation with same formatting as price
                    const compareElement = document.createElement('span');
                    compareElement.className = 'compare-at-price';
                    const originalText = priceSpan.textContent;
                    const newCompareAmount = (variant.compare_at_price / 100).toFixed(2);
                    const formattedCompareAmount = originalText.includes(',') ? newCompareAmount.replace('.', ',') : newCompareAmount;
                    compareElement.textContent = originalText.replace(/[\d,\.]+/, formattedCompareAmount);
                    priceSpan.parentNode.insertBefore(compareElement, priceSpan.nextSibling);
                  }
                }
                
                priceHtml = containerClone.outerHTML;
                if (showSavingsBadge) {
                  priceHtml += `<span class="savings-badge">${formatSavingsBadgeText()}</span>`;
                }
              } else if (originalContainer) {
                // Regular price - clone and update
                const containerClone = originalContainer.cloneNode(true);
                const priceSpan = containerClone.querySelector('.price');
                
                // Only update the numbers in the price, preserving all formatting
                if (priceSpan && priceSpan.textContent) {
                  const currentText = priceSpan.textContent;
                  const newAmount = (variant.price / 100).toFixed(2);
                  const formattedAmount = currentText.includes(',') ? newAmount.replace('.', ',') : newAmount;
                  priceSpan.textContent = currentText.replace(/[\d,\.]+/, formattedAmount);
                }
                
                // Clear compare price elements if they exist
                containerClone.querySelectorAll('.compare-at-price').forEach((compareElement) => {
                  const compareGroup = compareElement.closest('[role="group"]');
                  if (compareGroup && compareGroup.querySelector('.compare-at-price')) {
                    compareGroup.remove();
                  } else {
                    compareElement.remove();
                  }
                });
                
                priceHtml = containerClone.outerHTML;
              } else {
                // Fallback to basic structure
                priceHtml = `
                  <div ref="priceContainer">
                    <span class="price">${formattedPrice}</span>
                  </div>
                `;
              }
              
              priceWithSavings.innerHTML = priceHtml;

              if (!showCompareAtPrice) {
                priceWithSavings.querySelectorAll('.compare-at-price').forEach((compareElement) => {
                  const compareGroup = compareElement.closest('[role="group"]');
                  if (compareGroup?.querySelector('.compare-at-price')) {
                    compareGroup.remove();
                  } else {
                    compareElement.remove();
                  }
                });
              }

              if (!showSavingsBadge) {
                priceWithSavings.querySelectorAll('.savings-badge').forEach((badge) => badge.remove());
              }
            }
            
            mockHtml.body.appendChild(newPriceElement);
          }
          
          const event = new VariantUpdateEvent(variantData, this.#currentVariantId || '', {
            html: mockHtml,
            productId: this.getProductId()
          });
          
          if (productPriceElement) {
            const closestSection = productPriceElement.closest('.shopify-section, dialog');
            
            // Dispatch directly on the section that ProductPrice is listening to
            if (closestSection) {
              closestSection.dispatchEvent(event);
            } else {
              // Fallback: dispatch on ProductPrice element itself
              productPriceElement.dispatchEvent(event);
            }
          } else {
            // Original fallback
            const productSection = document.querySelector('.shopify-section');
            if (productSection) {
              productSection.dispatchEvent(event);
            } else {
              document.dispatchEvent(event);
            }
          }
        }
      })
      .catch(() => {});
  }

  /**
   * Build request URL for fetching updated product data
   */
  buildRequestUrl() {
    const url = new URL(window.location.href);
    
    // Ensure we have the current variant parameter
    if (this.#currentVariantId) {
      url.searchParams.set('variant', this.#currentVariantId);
    }
    
    // Add sections parameter to get only the product section data
    url.searchParams.set('sections', 'main');
    
    return url.toString();
  }

  /**
   * Get product ID from page data or variant picker
   */
  getProductId() {
    const variantPicker = document.querySelector('variant-picker');
    return (variantPicker instanceof HTMLElement ? variantPicker.dataset.productId : undefined) || '';
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new UrlVariantWatcher());
} else {
  new UrlVariantWatcher();
}
